import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { dropdownFilterMatchesSearch } from '@/utils/associationTypeOptions';

import type { DropdownOption, FilterValue, MenuCoords } from '../types';

const TCES_OPERATORS: { operator: string; label: string }[] = [
  { operator: 'eq', label: 'Equals' },
  { operator: 'gt', label: 'Greater than' },
  { operator: 'lt', label: 'Less than' },
  { operator: 'gte', label: 'Greater or equal' },
  { operator: 'lte', label: 'Less or equal' },
];

export function isTcesFilterActive(
  filterValues: Record<string, string>,
  filterOperators: Record<string, string>,
): boolean {
  const hasValue = Boolean(filterValues.tces?.trim());
  const hasCustomOperator = Boolean(filterOperators.tces && filterOperators.tces !== 'eq');
  return hasValue || hasCustomOperator;
}

type GtpColumnFilterProps = {
  filterValues: Record<string, string>;
  filterOperators: Record<string, string>;
  dropdownOptions?: DropdownOption[];
  onFilterChange?: (columnId: string, value: string) => void;
  onFilterOperatorChange?: (columnId: string, operator: string) => void;
  onBatchFilterChange?: (mutator: (filters: FilterValue[]) => FilterValue[]) => void;
};

export default function GtpColumnFilter({
  filterValues,
  filterOperators,
  dropdownOptions,
  onFilterChange,
  onFilterOperatorChange,
}: GtpColumnFilterProps) {
  const classValue = filterValues.GTP || '';
  const tcesValue = filterValues.tces || '';
  const tcesOperator = filterOperators.tces || 'eq';
  const tcesActive = isTcesFilterActive(filterValues, filterOperators);

  const [showTcesMenu, setShowTcesMenu] = useState(false);
  const [showDropdownMenu, setShowDropdownMenu] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState<string[]>(() =>
    classValue ? classValue.split(',').filter(Boolean) : [],
  );
  const [menuCoords, setMenuCoords] = useState<MenuCoords>({ top: 0, left: 0 });
  const [dropdownCoords, setDropdownCoords] = useState<MenuCoords>({ top: 0, left: 0 });

  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const next = classValue ? classValue.split(',').filter(Boolean) : [];
    setSelectedItems(prev => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
  }, [classValue]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowTcesMenu(false);
      }
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        dropdownButtonRef.current &&
        !dropdownButtonRef.current.contains(event.target as Node)
      ) {
        setShowDropdownMenu(false);
        setSearchTerm('');
      }
    }

    function updatePosition() {
      if (showTcesMenu && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const spaceOnLeft = rect.left;
        const position = spaceOnLeft < 240 ? 'right' : 'left';
        setMenuCoords({
          top: rect.bottom + 4,
          left: position === 'left' ? rect.right - 240 : rect.left,
        });
      }
      if (showDropdownMenu && dropdownButtonRef.current) {
        const rect = dropdownButtonRef.current.getBoundingClientRect();
        setDropdownCoords({ top: rect.bottom + 4, left: rect.left });
      }
    }

    if (showTcesMenu || showDropdownMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showTcesMenu, showDropdownMenu]);

  useEffect(() => {
    if (showTcesMenu && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceOnLeft = rect.left;
      const position = spaceOnLeft < 240 ? 'right' : 'left';
      setMenuCoords({
        top: rect.bottom + 4,
        left: position === 'left' ? rect.right - 240 : rect.left,
      });
    }
  }, [showTcesMenu]);

  useEffect(() => {
    if (showDropdownMenu && dropdownButtonRef.current) {
      const rect = dropdownButtonRef.current.getBoundingClientRect();
      setDropdownCoords({ top: rect.bottom + 4, left: rect.left });
    }
  }, [showDropdownMenu]);

  const filteredDropdownOptions =
    dropdownOptions?.filter(option => dropdownFilterMatchesSearch(option, searchTerm)) || [];

  const handleToggleItem = (itemId: string) => {
    const next = selectedItems.includes(itemId)
      ? selectedItems.filter(id => id !== itemId)
      : [...selectedItems, itemId];
    setSelectedItems(next);
    onFilterChange?.('GTP', next.join(','));
  };

  const handleClearClass = useCallback(() => {
    setSelectedItems([]);
    onFilterChange?.('GTP', '');
  }, [onFilterChange]);

  const handleClearTces = useCallback(() => {
    onFilterChange?.('tces', '');
    onFilterOperatorChange?.('tces', 'eq');
  }, [onFilterChange, onFilterOperatorChange]);

  const classLabel = useMemo(() => {
    if (selectedItems.length === 0) return 'All';
    if (selectedItems.length === 1) {
      return dropdownOptions?.find(opt => opt.id === selectedItems[0])?.label || selectedItems[0];
    }
    return `${selectedItems.length} selected`;
  }, [dropdownOptions, selectedItems]);

  const renderTcesMenu = () => {
    if (!showTcesMenu) return null;
    return ReactDOM.createPortal(
      <div
        ref={menuRef}
        className="filter-operator-menu-portal"
        style={{
          position: 'fixed',
          top: `${menuCoords.top}px`,
          left: `${menuCoords.left}px`,
          zIndex: 99999,
          background: 'white',
          border: '1px solid #ddd',
          borderRadius: '4px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          minWidth: '240px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '10px 12px 8px',
            borderBottom: '1px solid #ddd',
            fontSize: 12,
            fontWeight: 600,
            color: '#555',
          }}
        >
          TCES
        </div>
        <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="number"
            value={tcesValue}
            onChange={e => onFilterChange?.('tces', e.target.value)}
            placeholder="Value…"
            className="filter-input"
            aria-label="TCES filter value"
            style={{ width: '100%', margin: 0 }}
            onClick={e => e.stopPropagation()}
          />
          {tcesValue ? (
            <button
              type="button"
              className="filter-clear-button"
              onClick={e => {
                e.stopPropagation();
                handleClearTces();
              }}
              title="Clear TCES filter"
              style={{ position: 'static', transform: 'none', margin: 0 }}
            >
              ×
            </button>
          ) : null}
        </div>
        {TCES_OPERATORS.map(opt => (
          <div
            key={opt.operator}
            className={`filter-operator-option ${tcesOperator === opt.operator ? 'active' : ''}`}
            onClick={() => onFilterOperatorChange?.('tces', opt.operator)}
          >
            {opt.label}
          </div>
        ))}
      </div>,
      document.body,
    );
  };

  const renderDropdownMenu = () => {
    if (!showDropdownMenu || !dropdownOptions) return null;
    return ReactDOM.createPortal(
      <div
        ref={dropdownRef}
        className="filter-dropdown-menu-portal"
        style={{
          position: 'fixed',
          top: `${dropdownCoords.top}px`,
          left: `${dropdownCoords.left}px`,
          zIndex: 99999,
          background: 'white',
          border: '1px solid #ddd',
          borderRadius: '4px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          minWidth: '200px',
          maxHeight: '300px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search..."
            className="filter-input"
            style={{ width: '100%', margin: 0 }}
            onClick={e => e.stopPropagation()}
          />
        </div>
        <div style={{ overflowY: 'auto', flex: '1 1 auto' }}>
          {filteredDropdownOptions.length === 0 ? (
            <div style={{ padding: '8px', color: '#999', textAlign: 'center' }}>No options found</div>
          ) : (
            filteredDropdownOptions.map(option => (
              <div
                key={option.id}
                className="filter-dropdown-option"
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: selectedItems.includes(option.id) ? '#f0f0f0' : 'transparent',
                }}
                onClick={e => {
                  e.stopPropagation();
                  handleToggleItem(option.id);
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedItems.includes(option.id)}
                  onChange={() => {}}
                  style={{ cursor: 'pointer' }}
                />
                <span>{option.label}</span>
              </div>
            ))
          )}
        </div>
      </div>,
      document.body,
    );
  };

  return (
    <div className="column-filter" onClick={e => e.stopPropagation()}>
      <div className="filter-input-wrapper" style={{ gap: 2 }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
          <div
            ref={dropdownButtonRef}
            className="filter-input filter-select"
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              padding: classValue ? '4px 48px 4px 8px' : '4px 32px 4px 8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: 'white',
              minHeight: '32px',
              width: '100%',
            }}
            onClick={e => {
              e.stopPropagation();
              setShowTcesMenu(false);
              setShowDropdownMenu(!showDropdownMenu);
            }}
          >
            <span className="min-w-0 flex-1 truncate">{classLabel}</span>
          </div>
          {classValue ? (
            <button
              type="button"
              className="filter-clear-button"
              onClick={e => {
                e.stopPropagation();
                handleClearClass();
              }}
              title="Clear MeSH class filter"
              style={{ right: 28 }}
            >
              ×
            </button>
          ) : null}
        </div>
        <div
          className="filter-menu-wrapper"
          style={{ position: 'static', transform: 'none', top: 'auto', right: 'auto', flexShrink: 0 }}
        >
          <button
            ref={buttonRef}
            type="button"
            className="filter-menu-button"
            onClick={e => {
              e.stopPropagation();
              setShowDropdownMenu(false);
              setShowTcesMenu(!showTcesMenu);
            }}
            title="TCES filter"
            style={{
              marginBottom: 0,
              ...(tcesActive ? { color: '#1976d2', backgroundColor: '#e3f2fd' } : {}),
            }}
            aria-pressed={tcesActive}
          >
            ⋮
          </button>
        </div>
      </div>
      {renderTcesMenu()}
      {renderDropdownMenu()}
    </div>
  );
}
